import React, { useEffect, useState, useMemo, useRef } from "react";
import moment from "moment";
import {
  MessageSquare,
  Clock,
  CheckCircle2,
  Paperclip,
  X,
  Send,
  ChevronDown,
  FileText,
  FileDown,
  Eye,
  Check,
  Copy,
  RotateCcw,
  Plus,
  ArrowLeft,
} from "lucide-react";
import { Tooltip, Typography, Box } from "@mui/material";
import CentralizedTable from "../components/CentralizedTable";
import ApiClient from "../ApiClient";
import { notify } from "../utils/ToastUtils";
import TailwindModal from "../components/TailwindModal";
import LoaderIcon from "../components/LoaderIcon";
import { TICKET_CATEGORIES } from "../utils/constants";

export default function QueriesPage() {
  const [view, setView] = useState("list"); // 'list' | 'create'

  // Form State
  const [category, setCategory] = useState("General");
  const [categoryInput, setCategoryInput] = useState("");
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [priority, setPriority] = useState("Medium");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [attachments, setAttachments] = useState([]);

  // Tickets List & Detail State
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState({ fetch: true, submit: false, reply: false, action: false });
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // Reply State inside Modal
  const [replyMessage, setReplyMessage] = useState("");
  const [replyAttachments, setReplyAttachments] = useState([]);

  // In-Place Action State inside Ticket Modal ('idle' | 'reopen' | 'close')
  const [inlineAction, setInlineAction] = useState("idle");
  const [reopenReason, setReopenReason] = useState("");
  const [reopenAttachments, setReopenAttachments] = useState([]);
  const [closeNote, setCloseNote] = useState("");

  const fileInputRef = useRef(null);
  const replyFileInputRef = useRef(null);
  const reopenFileInputRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const [isCopied, setIsCopied] = useState(false);

  const handleCopyTicketId = () => {
    if (!selectedTicket?.REQUEST_ID) return;
    navigator.clipboard.writeText(selectedTicket.REQUEST_ID);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    if (isDetailModalOpen && selectedTicket) {
      const timer = setTimeout(() => {
        scrollToBottom();
      }, 120);
      return () => clearTimeout(timer);
    }
  }, [isDetailModalOpen, selectedTicket?.messages]);

  // Filter fixed standard categories based on search input
  const filteredCategories = useMemo(() => {
    if (!categoryInput.trim()) return TICKET_CATEGORIES;
    return TICKET_CATEGORIES.filter((c) =>
      c.toLowerCase().includes(categoryInput.toLowerCase().trim())
    );
  }, [categoryInput]);

  const fetchMyTickets = async () => {
    setLoading((prev) => ({ ...prev, fetch: true }));
    try {
      const res = await ApiClient.get(`${process.env.REACT_APP_BASE_URL}/user/tickets/my-tickets`);
      setTickets(res?.data || []);
    } catch (err) {
      console.error("Error fetching my tickets", err);
      notify.error("Failed to load tickets");
    } finally {
      setLoading((prev) => ({ ...prev, fetch: false }));
    }
  };

  useEffect(() => {
    fetchMyTickets();
  }, []);

  // File Upload Handlers (uploads files via Multer to native storage and stores metadata)
  const handleFileUpload = async (e, target = "form") => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        notify.warning(`File "${file.name}" exceeds 10MB limit`);
        return;
      }
    }

    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file);
    });

    try {
      const res = await ApiClient.post(
        `${process.env.REACT_APP_BASE_URL}/user/tickets/upload`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      const uploadedFiles = res?.data?.files || [];
      if (target === "reply") {
        setReplyAttachments((prev) => [...prev, ...uploadedFiles]);
      } else if (target === "reopen") {
        setReopenAttachments((prev) => [...prev, ...uploadedFiles]);
      } else {
        setAttachments((prev) => [...prev, ...uploadedFiles]);
      }
    } catch (err) {
      console.error("Error uploading ticket files", err);
      notify.error("Failed to upload file(s)");
    } finally {
      e.target.value = null;
    }
  };

  const removeAttachment = (index, target = "form") => {
    if (target === "reply") {
      setReplyAttachments((prev) => prev.filter((_, i) => i !== index));
    } else if (target === "reopen") {
      setReopenAttachments((prev) => prev.filter((_, i) => i !== index));
    } else {
      setAttachments((prev) => prev.filter((_, i) => i !== index));
    }
  };

  // Download Attachment Helper
  const handleDownloadAttachment = async (file, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    try {
      const fileName = file.name || file.filename || "attachment";
      if (file.url) {
        let fullUrl = file.url;
        if (!fullUrl.startsWith("http://") && !fullUrl.startsWith("https://")) {
          const publicBase =
            process.env.REACT_APP_PUBLIC_URL ||
            (process.env.REACT_APP_BASE_URL
              ? process.env.REACT_APP_BASE_URL.replace(/\/api\/?$/, "")
              : "");
          fullUrl = `${publicBase}${fullUrl.startsWith("/") ? "" : "/"}${fullUrl}`;
        }
        const response = await fetch(fullUrl);
        if (!response.ok) throw new Error("File could not be downloaded from server");
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      } else if (file.data) {
        const dataUrl = file.data.startsWith("data:")
          ? file.data
          : `data:${file.type || "application/octet-stream"};base64,${file.data}`;
        const response = await fetch(dataUrl);
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      } else {
        notify.warning("No file URL or data found");
      }
    } catch (err) {
      console.error("Failed to download attachment", err);
      notify.error("Failed to download file");
    }
  };

  // Reset Form Helper
  const resetForm = () => {
    setSubject("");
    setDescription("");
    setAttachments([]);
    setCategory("General");
    setCategoryInput("");
    setIsCategoryDropdownOpen(false);
    setPriority("Medium");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Submit New Support Ticket
  const handleSubmitTicket = async (e) => {
    e.preventDefault();
    if (!subject.trim()) {
      notify.warning("Please provide a subject");
      return;
    }
    if (!description.trim()) {
      notify.warning("Please provide a description");
      return;
    }

    setLoading((prev) => ({ ...prev, submit: true }));
    try {
      const payload = {
        category: category || "General",
        priority,
        subject: subject.trim(),
        description: description.trim(),
        attachments,
      };

      await ApiClient.post(`${process.env.REACT_APP_BASE_URL}/user/tickets/create`, payload);
      notify.success("Support ticket created successfully!");

      // Immediately reset all form values
      resetForm();

      // Refresh tickets list & switch to list view
      fetchMyTickets();
      setView("list");
    } catch (err) {
      console.error("Error creating ticket", err);
      notify.error(err?.response?.data?.message || "Failed to create ticket");
    } finally {
      setLoading((prev) => ({ ...prev, submit: false }));
    }
  };

  // Open Ticket Detail Modal
  const handleOpenTicketDetail = async (ticketId) => {
    try {
      const res = await ApiClient.get(`${process.env.REACT_APP_BASE_URL}/user/tickets/${ticketId}`);
      setSelectedTicket(res?.data || null);
      setReplyMessage("");
      setReplyAttachments([]);
      setInlineAction("idle");
      setReopenReason("");
      setReopenAttachments([]);
      setCloseNote("");
      if (replyFileInputRef.current) {
        replyFileInputRef.current.value = "";
      }
      if (reopenFileInputRef.current) {
        reopenFileInputRef.current.value = "";
      }
      setIsDetailModalOpen(true);
    } catch (err) {
      console.error("Error fetching ticket detail", err);
      notify.error("Could not open ticket details");
    }
  };

  // Close Ticket Detail Modal & Clear all inputs
  const handleCloseDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedTicket(null);
    setReplyMessage("");
    setReplyAttachments([]);
    setInlineAction("idle");
    setReopenReason("");
    setReopenAttachments([]);
    setCloseNote("");
    if (replyFileInputRef.current) {
      replyFileInputRef.current.value = "";
    }
    if (reopenFileInputRef.current) {
      reopenFileInputRef.current.value = "";
    }
  };

  // Reply inside Modal
  const handleSendReply = async () => {
    if (!replyMessage.trim() && replyAttachments.length === 0) {
      notify.warning("Please write a message or attach a file");
      return;
    }

    setLoading((prev) => ({ ...prev, reply: true }));
    try {
      await ApiClient.post(`${process.env.REACT_APP_BASE_URL}/user/tickets/${selectedTicket.REQUEST_ID}/reply`, {
        message: replyMessage.trim(),
        attachments: replyAttachments,
      });

      notify.success("Reply sent successfully!");
      handleCloseDetailModal();
      fetchMyTickets();
    } catch (err) {
      console.error("Error sending reply", err);
      notify.error("Failed to send reply");
    } finally {
      setLoading((prev) => ({ ...prev, reply: false }));
    }
  };

  // Close Ticket (Employee confirmed resolution in-place)
  const handleConfirmClose = async () => {
    if (!selectedTicket) return;
    setLoading((prev) => ({ ...prev, action: true }));
    try {
      await ApiClient.put(`${process.env.REACT_APP_BASE_URL}/user/tickets/${selectedTicket.REQUEST_ID}/close`, {
        note: closeNote.trim(),
      });
      notify.success("Ticket closed successfully.");
      handleCloseDetailModal();
      fetchMyTickets();
    } catch (err) {
      console.error("Error closing ticket", err);
      notify.error("Failed to close ticket");
    } finally {
      setLoading((prev) => ({ ...prev, action: false }));
    }
  };

  // Reopen Ticket (Employee requested further help in-place)
  const handleConfirmReopen = async () => {
    if (!selectedTicket) return;
    if (!reopenReason.trim()) {
      notify.warning("Please provide a reason for reopening");
      return;
    }
    setLoading((prev) => ({ ...prev, action: true }));
    try {
      await ApiClient.put(`${process.env.REACT_APP_BASE_URL}/user/tickets/${selectedTicket.REQUEST_ID}/reopen`, {
        reason: reopenReason.trim(),
        attachments: reopenAttachments,
      });
      notify.success("Ticket reopened.");
      handleCloseDetailModal();
      fetchMyTickets();
    } catch (err) {
      console.error("Error reopening ticket", err);
      notify.error("Failed to reopen ticket");
    } finally {
      setLoading((prev) => ({ ...prev, action: false }));
    }
  };

  // Priority Dot Renderer
  const renderPriorityBadge = (p) => {
    if (p === "High") {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold bg-[#fef2f2] text-[#e11d48]">
          <span className="w-2 h-2 rounded-full bg-[#e11d48] shrink-0" />
          High
        </span>
      );
    }
    if (p === "Medium") {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold bg-[#fff7ed] text-[#c2410c]">
          <span className="w-2 h-2 rounded-full bg-[#ea580c] shrink-0" />
          Medium
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold bg-[#eef4ff] text-[#2563eb]">
        <span className="w-2 h-2 rounded-full bg-[#2563eb] shrink-0" />
        Low
      </span>
    );
  };

  // Status Badge Renderer
  const renderStatusBadge = (status) => {
    switch (status) {
      case "New":
      case "Pending":
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold bg-[#F1F5F9] text-[#64748B]">
            <span className="w-2 h-2 rounded-full bg-[#64748B] shrink-0" />
            New
          </span>
        );
      case "In Progress":
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold bg-[#EFF6FF] text-[#2563EB]">
            <span className="w-2 h-2 rounded-full bg-[#2563EB] shrink-0" />
            In Progress
          </span>
        );
      case "Waiting for Employee":
      case "Waiting on you":
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold bg-[#FEFCE8] text-[#A16207]">
            <span className="w-2 h-2 rounded-full bg-[#CA8A04] shrink-0" />
            Waiting on You
          </span>
        );
      case "Resolved":
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold bg-[#ECFDF5] text-[#059669]">
            <span className="w-2 h-2 rounded-full bg-[#059669] shrink-0" />
            Resolved
          </span>
        );
      case "Closed":
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium bg-[#F8FAFC] text-[#94A3B8] border border-slate-200/80">
            <span className="w-2 h-2 rounded-full bg-[#94A3B8] shrink-0" />
            Closed
          </span>
        );
    }
  };

  // Table Columns for My Tickets
  const columns = [
    {
      headerName: "Ticket ID",
      field: "REQUEST_ID",
      width: 180,
      renderCell: (params) => {
        const ticketId = params?.row?.REQUEST_ID ? `#${params.row.REQUEST_ID}` : "-";
        return (
          <Tooltip title={ticketId} arrow placement="top">
            <Typography
              variant="body2"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 1,
                WebkitBoxOrient: "vertical",
                whiteSpace: "normal",
                lineHeight: 1.4,
                cursor: "pointer",
                fontFamily: "monospace",
                fontSize: "0.75rem",
                fontWeight: 700,
                color: "#2563eb",
                bgcolor: "#eff6ff",
                border: "1px solid #dbeafe",
                px: 1,
                py: 0.25,
                borderRadius: "6px",
                textAlign: "center",
              }}
            >
              {ticketId}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      headerName: "Category",
      field: "CATEGORY",
      width: 140,
      renderCell: (params) => {
        const cat = params?.row?.CATEGORY || "General";
        return (
          <Tooltip title={cat} arrow placement="top">
            <Typography
              variant="body2"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 1,
                WebkitBoxOrient: "vertical",
                whiteSpace: "normal",
                lineHeight: 1.4,
                fontSize: "0.75rem",
                fontWeight: 600,
                color: "#334155",
                cursor: "pointer",
                width: "100%",
              }}
            >
              {cat}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      headerName: "Subject",
      field: "SUBJECT",
      width: 280,
      renderCell: (params) => {
        const subject = params?.row?.SUBJECT || "Support Query";
        const lastMsg = params?.row?.last_message || "";
        const fullTooltip = lastMsg ? `${subject} — ${lastMsg}` : subject;
        return (
          <Tooltip title={fullTooltip} arrow placement="top">
            <div className="flex flex-col justify-center h-full py-1 w-full overflow-hidden">
              <Typography
                variant="body2"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: "vertical",
                  whiteSpace: "normal",
                  lineHeight: 1.4,
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  color: "#1e293b",
                  cursor: "pointer",
                  width: "100%",
                }}
              >
                {subject}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: "vertical",
                  whiteSpace: "normal",
                  lineHeight: 1.3,
                  fontSize: "0.6875rem",
                  color: "#94a3b8",
                  width: "100%",
                }}
              >
                {lastMsg || "No message history"}
              </Typography>
            </div>
          </Tooltip>
        );
      },
    },
    {
      headerName: "Priority",
      field: "PRIORITY",
      width: 110,
      renderCell: (params) => (
        <Tooltip title={`Priority: ${params?.row?.PRIORITY || "Low"}`} arrow placement="top">
          <div className="truncate max-w-full">
            {renderPriorityBadge(params?.row?.PRIORITY)}
          </div>
        </Tooltip>
      ),
    },
    {
      headerName: "Status",
      field: "STATUS",
      width: 150,
      renderCell: (params) => (
        <Tooltip title={`Status: ${params?.row?.STATUS || "New"}`} arrow placement="top">
          <div className="truncate max-w-full">
            {renderStatusBadge(params?.row?.STATUS)}
          </div>
        </Tooltip>
      ),
    },
    {
      headerName: "Raised Date",
      field: "RAISED_DATE",
      width: 140,
      renderCell: (params) => {
        const fullDateStr = moment(params?.row?.RAISED_DATE).format("DD MMM YYYY, hh:mm A");
        return (
          <Tooltip title={fullDateStr} arrow placement="top">
            <div className="flex flex-col justify-center h-full w-full overflow-hidden">
              <Typography
                variant="body2"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  color: "#334155",
                }}
              >
                {moment(params?.row?.RAISED_DATE).format("DD MMM YYYY")}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize: "0.625rem",
                  fontWeight: 500,
                  color: "#94a3b8",
                }}
              >
                {moment(params?.row?.RAISED_DATE).format("hh:mm A")}
              </Typography>
            </div>
          </Tooltip>
        );
      },
    },
    {
      headerName: "Action",
      field: "actions",
      width: 140,
      sortable: false,
      renderCell: (params) => {
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              width: "100%",
            }}
          >
            <Tooltip title="View Ticket Details" arrow placement="top">
              <button
                type="button"
                onClick={() => handleOpenTicketDetail(params?.row?.REQUEST_ID)}
                disabled={loading.fetch}
                className="flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border border-blue-600 text-blue-600 hover:bg-blue-50 transition-colors whitespace-nowrap shadow-2xs"
              >
                <Eye size={13} className="shrink-0" />
                <span>Open Ticket</span>
              </button>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  const wordCount = description.trim().split(/\s+/).filter(Boolean).length;

  return (
    <div className="bg-transparent w-full h-full flex flex-col overflow-y-auto overflow-x-hidden">
      <div className="max-w-[1700px] mx-auto px-2 sm:px-4 py-2 sm:py-2 flex-1 w-full flex flex-col mb-2 space-y-3">
        
        {/* VIEW 1: MY TICKETS TABLE */}
        {view === "list" && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden p-0">
            <CentralizedTable
              isLoading={loading.fetch}
              renderTopToolbarCustomActions={() => (
                <div className="flex items-center gap-3 py-2">
                  <div className="p-2.5 rounded-xl bg-blue-50 text-blue-600">
                    <MessageSquare size={18} />
                  </div>
                  <div>
                    <h3 className="text-[15px] font-bold text-slate-800">
                      My Tickets ({tickets.length})
                    </h3>
                    <p className="text-xs text-slate-500">Track all your submitted queries and resolutions.</p>
                  </div>
                </div>
              )}
              customInternalActions={() => (
                <button
                  type="button"
                  onClick={() => setView("create")}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg shadow-2xs transition-all ml-1 cursor-pointer shrink-0 whitespace-nowrap"
                >
                  <Plus size={14} />
                  <span>Create Ticket</span>
                </button>
              )}
              getRowId={(row) => row.REQUEST_ID}
              rows={tickets}
              columns={columns}
              rowHeight={52}
              columnHeaderHeight={44}
              autoHeight
              enableColumnPinning={true}
              muiTableContainerProps={{
                sx: {
                  minHeight: "auto",
                },
              }}
              muiTablePaperProps={{
                sx: {
                  height: "auto",
                },
              }}
              muiTableHeadCellProps={({ column }) => ({
                sx: {
                  boxShadow: "none !important",
                  "&::before": {
                    boxShadow:
                      column.id === "actions"
                        ? "-4px 0 10px rgba(0,0,0,0.05) !important"
                        : "none !important",
                  },
                },
              })}
              muiTableBodyCellProps={({ cell }) => ({
                sx: {
                  py: "8px",
                  borderBottom: "1px solid #e2e8f0",
                  px: cell.column.getIndex() === 0 ? "16px" : "3px",
                  overflow: cell.column.id === "actions" ? "visible" : "hidden",
                  textOverflow: cell.column.id === "actions" ? "clip" : "ellipsis",
                  backgroundColor:
                    cell.column.id === "actions" ? "#fff !important" : "inherit",
                  boxShadow: "none !important",
                  "&::before": {
                    boxShadow:
                      cell.column.id === "actions"
                        ? "-4px 0 10px rgba(0,0,0,0.05) !important"
                        : "none !important",
                  },
                },
              })}
              initialState={{
                pagination: { paginationModel: { pageSize: 10, page: 0 } },
                columnPinning: { right: ["actions"] },
              }}
            />
          </div>
        )}

        {/* VIEW 2: CREATE SUPPORT TICKET (DRILL-DOWN) */}
        {view === "create" && (
          <form onSubmit={handleSubmitTicket} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 sm:p-5 w-full space-y-3.5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-2.5 mb-0.5 border-b border-slate-100 gap-2.5">
              <div className="flex items-center gap-2.5 sm:gap-3">
                <button
                  type="button"
                  onClick={() => setView("list")}
                  className="p-1.5 sm:p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-xl transition-all cursor-pointer -ml-1 border border-slate-200/80 shadow-2xs bg-white"
                  title="Back to Tickets"
                >
                  <ArrowLeft size={18} />
                </button>
                <div>
                  <h2 className="text-sm sm:text-base font-bold text-slate-800">Submit a Support Ticket</h2>
                  <p className="text-[11px] text-slate-500">All fields marked with * are required.</p>
                </div>
              </div>

              {/* Action Buttons in Header */}
              <div className="flex items-center gap-2.5 shrink-0 self-end sm:self-auto">
                {(subject || description || attachments.length > 0 || category !== "General" || priority !== "Medium") && (
                  <button
                    type="button"
                    onClick={resetForm}
                    disabled={loading.submit}
                    className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-slate-600 hover:text-slate-900 bg-slate-100 hover:bg-slate-200/80 rounded-lg transition-colors border border-slate-200/80"
                  >
                    <RotateCcw size={13} /> Clear Form
                  </button>
                )}
                <button
                  type="submit"
                  disabled={loading.submit || !subject.trim() || !description.trim()}
                  className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-semibold rounded-lg shadow-sm transition-all"
                >
                  {loading.submit ? <LoaderIcon className="w-3.5 h-3.5" /> : <Send size={13} />}
                  {loading.submit ? "Submitting..." : "Submit Support Ticket"}
                </button>
              </div>
            </div>

            {/* Category Combobox + Priority */}
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
              {/* Fixed Category Combobox */}
              <div className="sm:col-span-7 relative">
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Category <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <div
                    onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
                    className="w-full h-[38px] flex items-center justify-between px-3 bg-white border border-slate-200 rounded-lg text-xs font-medium text-slate-800 cursor-pointer shadow-sm hover:border-slate-300 focus:ring-2 focus:ring-blue-500/20"
                  >
                    <span className="truncate">{category || "Select Category"}</span>
                    <ChevronDown size={14} className="text-slate-400 shrink-0" />
                  </div>

                  {/* Dropdown with Search */}
                  {isCategoryDropdownOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-20"
                        onClick={() => setIsCategoryDropdownOpen(false)}
                      />
                      <div className="absolute left-0 right-0 mt-1 bg-white rounded-lg shadow-xl border border-slate-200 py-1 z-30 flex flex-col animate-in fade-in zoom-in-95 duration-150 overflow-hidden">
                        {/* Search input (pinned at top) */}
                        <div className="px-2.5 pb-2 pt-1 border-b border-slate-100 shrink-0">
                          <input
                            type="text"
                            placeholder="Search category..."
                            value={categoryInput}
                            onChange={(e) => setCategoryInput(e.target.value)}
                            className="w-full px-2.5 py-1.5 border border-slate-200 rounded-lg text-xs text-slate-800 focus:outline-none focus:border-blue-500 bg-slate-50"
                            autoFocus
                          />
                        </div>

                        {/* Options List (Scrollable only) */}
                        <div className="py-1 max-h-48 overflow-y-auto custom-scrollbar">
                          {filteredCategories.map((cat) => (
                            <button
                              key={cat}
                              type="button"
                              onClick={() => {
                                setCategory(cat);
                                setCategoryInput("");
                                setIsCategoryDropdownOpen(false);
                              }}
                              className={`w-full flex items-center justify-between px-3 py-1.5 text-xs text-left hover:bg-blue-50/80 transition-colors ${
                                category === cat ? "bg-blue-50 font-bold text-blue-600" : "text-slate-700"
                              }`}
                            >
                              <span>{cat}</span>
                              {category === cat && <Check size={13} className="text-blue-600" />}
                            </button>
                          ))}

                          {filteredCategories.length === 0 && (
                            <div className="px-3 py-2 text-xs text-slate-400 italic text-center">
                              No matching categories
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Priority Selector with Colored Dots */}
              <div className="sm:col-span-5">
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Priority <span className="text-rose-500">*</span>
                </label>
                <div className="grid grid-cols-3 gap-1 bg-slate-100 p-0.5 rounded-lg border border-slate-200/80 h-[38px] items-center">
                  {["Low", "Medium", "High"].map((p) => {
                    const isSelected = priority === p;
                    const dotColor =
                      p === "High" ? "bg-rose-500" : p === "Medium" ? "bg-amber-500" : "bg-blue-500";
                    return (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setPriority(p)}
                        className={`h-full flex items-center justify-center gap-1.5 rounded-md text-xs font-semibold transition-all ${
                          isSelected
                            ? "bg-white text-slate-800 shadow-sm border border-slate-200/60 font-bold"
                            : "text-slate-500 hover:text-slate-800"
                        }`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
                        {p}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Subject */}
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Subject / Title <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                required
                maxLength={200}
                placeholder="Brief summary of the issue (e.g. Leave balance discrepancy for August)"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full h-[38px] px-3 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm"
              />
            </div>

            {/* Description */}
            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="block text-xs font-bold text-slate-700">
                  Detailed Description <span className="text-rose-500">*</span>
                </label>
                <span
                  className={`text-[11px] font-medium ${
                    wordCount > 500 ? "text-rose-500" : "text-slate-400"
                  }`}
                >
                  {wordCount} / 500 words
                </span>
              </div>
              <textarea
                rows={4}
                required
                placeholder="Provide full context, dates, error messages, or details to help HR investigate quickly..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full p-3 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all resize-none shadow-sm"
              />
            </div>

            {/* Attachments Upload Zone */}
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Attachments (Screenshots / Documents)
              </label>
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-slate-200 hover:border-blue-400 rounded-lg p-3 sm:p-3.5 flex flex-col items-center justify-center cursor-pointer bg-slate-50/50 hover:bg-blue-50/30 transition-all text-center group"
              >
                <div className="w-8 h-8 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-400 group-hover:text-blue-600 group-hover:border-blue-200 transition-colors shadow-sm mb-1.5">
                  <Paperclip size={15} />
                </div>
                <span className="text-xs font-bold text-slate-700 group-hover:text-blue-600">
                  Click or drag files here to upload
                </span>
                <span className="text-[10px] text-slate-400 mt-0.5">
                  Supports PNG, JPG, PDF, XLSX (Max 10MB per file)
                </span>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => handleFileUpload(e, false)}
                />
              </div>

              {/* Uploaded Files Chips */}
              {attachments.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2.5">
                  {attachments.map((att, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-1.5 bg-blue-50 border border-blue-200 text-blue-800 px-2.5 py-1 rounded-lg text-xs font-medium shadow-sm"
                    >
                      <FileText size={12} className="text-blue-600" />
                      <span className="max-w-[150px] truncate">{att.name}</span>
                      <span className="text-[10px] text-blue-500">({att.size})</span>
                      <button
                        type="button"
                        onClick={() => removeAttachment(idx, false)}
                        className="text-blue-400 hover:text-rose-600 ml-0.5"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </form>
        )}

        {/* Ticket Details & Conversation Modal (Minimal Width matching QueriesHR.js) */}
        <TailwindModal
          isOpen={isDetailModalOpen}
          toggle={handleCloseDetailModal}
          maxWidth="2xl"
        >
          {selectedTicket && (
            <div className="p-0 flex flex-col h-[85vh] max-h-[85vh] rounded-2xl bg-white overflow-hidden shadow-2xl">
              {/* Modal Header */}
              <div className="px-4 py-2.5 sm:px-5 sm:py-3 border-b border-slate-100 flex items-center justify-between bg-white shrink-0">
                <div className="flex flex-wrap items-center gap-2 sm:gap-2.5">
                  {/* ID with Copy Button */}
                  <div className="inline-flex items-center gap-1.5 bg-blue-50/80 border border-blue-200/80 text-blue-700 font-mono text-xs font-bold px-2 py-0.5 rounded-lg shadow-xs">
                    <span>#{selectedTicket.REQUEST_ID}</span>
                    <Tooltip title={isCopied ? "Copied!" : "Copy Ticket ID"}>
                      <button
                        type="button"
                        onClick={handleCopyTicketId}
                        className="text-blue-500 hover:text-blue-800 transition-colors p-0.5 rounded"
                      >
                        {isCopied ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} />}
                      </button>
                    </Tooltip>
                  </div>

                  {/* Category Pill */}
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-lg text-xs font-bold bg-slate-100 text-slate-700 border border-slate-200/80">
                    {selectedTicket.CATEGORY || "General"}
                  </span>

                  {/* Priority Pill */}
                  {renderPriorityBadge(selectedTicket.PRIORITY)}

                  {/* Status Pill */}
                  {renderStatusBadge(selectedTicket.STATUS)}
                </div>

                {/* Close Button */}
                <button
                  type="button"
                  onClick={handleCloseDetailModal}
                  className="w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors ml-2"
                >
                  <X size={16} />
                </button>
              </div>

              {/* Modal Body */}
              <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-5 space-y-3.5 bg-white custom-scrollbar">
                {/* Top Support Query Card */}
                <div className="bg-[#f8faff] border border-slate-200/80 rounded-xl p-3 sm:p-3.5 space-y-1.5 shrink-0">
                  <h3 className="text-sm sm:text-base font-bold text-[#0f2d59]">
                    {selectedTicket.SUBJECT || "Support Query"}
                  </h3>
                  <div className="text-xs text-slate-400 font-medium flex flex-wrap items-center gap-1.5">
                    <span>
                      Raised on:{" "}
                      <strong className="text-slate-700 font-bold">
                        {moment(selectedTicket.RAISED_DATE).format("DD MMM YYYY, hh:mm A")}
                      </strong>
                    </span>
                    {selectedTicket.ASSIGNED_TO_NAME && (
                      <>
                        <span className="text-slate-300">|</span>
                        <span>
                          Assigned HR:{" "}
                          <strong className="text-slate-700 font-bold">
                            {selectedTicket.ASSIGNED_TO_NAME}
                          </strong>
                        </span>
                      </>
                    )}
                  </div>
                </div>

                {/* Conversation Timeline */}
                <div className="flex-1 min-h-0 flex flex-col space-y-2">
                  <div className="flex items-center justify-between px-1 shrink-0">
                    <h4 className="text-xs sm:text-sm font-bold text-[#0f2d59]">
                      Conversation History
                    </h4>
                    <span className="text-xs text-slate-400 font-medium">
                      {(selectedTicket.messages || []).length} messages
                    </span>
                  </div>

                  {/* Scrollable Message Box with continuous connector line */}
                  <div
                    ref={messagesContainerRef}
                    className="space-y-3.5 pt-1 pb-1"
                  >
                    {(selectedTicket.messages || []).map((msg, index, arr) => {
                      const isEmployee = msg.SENDER_ROLE === "Employee" || msg.TYPE === "EMP_QUERY";
                      const isAudit =
                        msg.TYPE &&
                        [
                          "STATUS_CHANGE",
                          "CLOSED",
                          "AUTO_CLOSED",
                          "ASSIGNMENT",
                          "PRIORITY_CHANGE",
                        ].includes(msg.TYPE);

                      if (isAudit) {
                        return (
                          <div
                            key={index}
                            className="relative flex items-center justify-center gap-2 py-0.5 text-xs text-slate-500 font-medium"
                          >
                            {index !== arr.length - 1 && (
                              <span
                                className="absolute left-[15px] top-0 bottom-[-16px] w-[2px] bg-slate-100"
                                aria-hidden="true"
                              />
                            )}
                            <span className="w-1.5 h-1.5 rounded-full bg-slate-300 relative z-10" />
                            <span className="bg-slate-50 px-2.5 py-0.5 rounded-full text-[11px] text-slate-600 border border-slate-200 relative z-10">
                              {msg.MESSAGE}
                            </span>
                            <span className="text-[10px] text-slate-400 relative z-10">
                              ({moment(msg.RAISED_DATE).format("DD MMM, hh:mm A")})
                            </span>
                          </div>
                        );
                      }

                      const authorInitial = isEmployee
                        ? (msg.RAISED_BY || selectedTicket.RAISED_BY || "You").charAt(0).toUpperCase()
                        : "H";
                      const senderName = isEmployee
                        ? msg.RAISED_BY || selectedTicket.RAISED_BY || "You"
                        : "HR Support";

                      return (
                        <div key={index} className="relative flex items-start gap-3 group">
                          {/* Continuous connector line between messages */}
                          {index !== arr.length - 1 && (
                            <span
                              className="absolute left-[14px] top-[26px] bottom-[-20px] w-[2px] bg-slate-100"
                              aria-hidden="true"
                            />
                          )}

                          {/* Avatar Circle with Initial */}
                          <div
                            className={`relative z-10 w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center shrink-0 font-bold text-xs shadow-xs ${
                              isEmployee
                                ? "bg-[#a7f3d0] text-[#065f46]"
                                : "bg-[#bfdbfe] text-[#1e40af]"
                            }`}
                          >
                            {authorInitial}
                          </div>

                          {/* Message Header & Bubble */}
                          <div className="flex-1 min-w-0 space-y-1">
                            {/* Header: Sender Name, Status Badge & Date */}
                            <div className="flex items-center gap-2 text-xs flex-wrap">
                              <span className="font-bold text-slate-800 uppercase tracking-wide text-[11px] sm:text-xs">
                                {senderName}
                              </span>
                              {msg.TYPE === "RESOLVED" && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                                  <CheckCircle2 size={11} className="text-emerald-600" /> Resolution Note
                                </span>
                              )}
                              {msg.TYPE === "REOPEN" && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200">
                                  <RotateCcw size={11} className="text-amber-600" /> Reopened
                                </span>
                              )}
                              <span className="text-[10px] sm:text-[11px] text-slate-400 font-normal ml-auto">
                                {moment(msg.RAISED_DATE).format("DD MMM YYYY, hh:mm A")}
                              </span>
                            </div>

                            {/* Message Bubble Card */}
                            <div className="bg-[#f8fafc] border border-slate-100 rounded-xl px-3.5 py-2 sm:px-4 sm:py-2.5 text-xs text-slate-800 font-normal leading-relaxed">
                              <p className="whitespace-pre-wrap leading-relaxed">
                                {msg.MESSAGE}
                              </p>

                              {/* Attachments */}
                              {msg.ATTACHMENTS && msg.ATTACHMENTS.length > 0 && (
                                <div className="mt-2 pt-1.5 border-t border-slate-200/60 flex flex-wrap gap-1.5">
                                  {msg.ATTACHMENTS.map((file, fIdx) => (
                                    <button
                                      key={fIdx}
                                      type="button"
                                      onClick={(e) => handleDownloadAttachment(file, e)}
                                      className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white hover:bg-blue-50 text-slate-700 hover:text-blue-700 rounded-lg text-[11px] font-semibold border border-slate-200 hover:border-blue-300 transition-all shadow-xs cursor-pointer group"
                                      title={`Download ${file.name || "file"}`}
                                    >
                                      <FileText size={12} className="text-blue-600 group-hover:text-blue-700" />
                                      <span className="truncate max-w-[140px]">{file.name || file.filename || "file"}</span>
                                      {file.size && <span className="text-[10px] text-slate-400">({file.size})</span>}
                                      <FileDown size={12} className="text-slate-400 group-hover:text-blue-600 ml-0.5" />
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                </div>
              </div>

              {/* Modal Footer / In-Place Action Panel */}
              {selectedTicket.STATUS === "Closed" ? (
                <div className="p-3.5 text-center text-xs text-slate-500 bg-slate-50 border-t border-slate-100 font-medium">
                  This ticket is closed and no further replies can be added.
                </div>
              ) : selectedTicket.STATUS === "Resolved" ? (
                inlineAction === "reopen" ? (
                  /* ↩ In-Place Reopen Composer */
                  <div className="p-3.5 sm:p-4 border-t border-amber-200 bg-amber-50/40 shrink-0 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-xs font-bold text-amber-900">
                        <RotateCcw size={14} className="text-amber-600" />
                        <span>Reopen Ticket</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setInlineAction("idle");
                          setReopenReason("");
                          setReopenAttachments([]);
                        }}
                        className="text-slate-400 hover:text-slate-700 p-1 rounded-lg transition-colors"
                      >
                        <X size={14} />
                      </button>
                    </div>

                    <div>
                      <label className="block text-[11px] font-bold text-slate-700 mb-1.5">
                        Reason for reopening <span className="text-rose-500">*</span>
                      </label>
                      <textarea
                        rows={2}
                        required
                        placeholder="Please explain why you need further assistance..."
                        value={reopenReason}
                        onChange={(e) => setReopenReason(e.target.value)}
                        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all resize-none shadow-2xs"
                        autoFocus
                      />
                    </div>

                    {/* Attachments (Optional) */}
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <label className="text-[11px] font-bold text-slate-700">Attachments (Optional)</label>
                        <button
                          type="button"
                          onClick={() => reopenFileInputRef.current?.click()}
                          className="flex items-center gap-1.5 text-xs font-semibold text-amber-700 hover:text-amber-800 transition-colors px-2 py-1 rounded-lg hover:bg-amber-100/50"
                        >
                          <Paperclip size={13} /> Attach files
                        </button>
                        <input
                          ref={reopenFileInputRef}
                          type="file"
                          multiple
                          className="hidden"
                          onChange={(e) => handleFileUpload(e, "reopen")}
                        />
                      </div>

                      {reopenAttachments.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {reopenAttachments.map((att, idx) => (
                            <div
                              key={idx}
                              className="flex items-center gap-1.5 bg-white border border-amber-200 text-amber-900 px-2.5 py-1 rounded-lg text-xs shadow-2xs"
                            >
                              <FileText size={12} className="text-amber-600" />
                              <span className="max-w-[140px] truncate">{att.name}</span>
                              <button
                                type="button"
                                onClick={() => removeAttachment(idx, "reopen")}
                                className="text-slate-400 hover:text-rose-500 ml-0.5"
                              >
                                <X size={12} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-end gap-2 pt-1.5 border-t border-amber-200/60">
                      <button
                        type="button"
                        onClick={() => {
                          setInlineAction("idle");
                          setReopenReason("");
                          setReopenAttachments([]);
                        }}
                        disabled={loading.action}
                        className="px-3.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-200/60 rounded-xl transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleConfirmReopen}
                        disabled={loading.action || !reopenReason.trim()}
                        className="flex items-center gap-1.5 px-4 py-1.5 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-bold rounded-lg shadow-2xs transition-all"
                      >
                        {loading.action ? <LoaderIcon className="w-3.5 h-3.5" /> : <RotateCcw size={13} />}
                        {loading.action ? "Reopening..." : "Reopen & Send"}
                      </button>
                    </div>
                  </div>
                ) : inlineAction === "close" ? (
                  /* ✓ In-Place Close Confirmation Section */
                  <div className="p-3.5 sm:p-4 border-t border-emerald-200 bg-emerald-50/40 shrink-0 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-xs font-bold text-emerald-900">
                        <CheckCircle2 size={15} className="text-emerald-600" />
                        <span>Close Ticket</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setInlineAction("idle");
                          setCloseNote("");
                        }}
                        className="text-slate-400 hover:text-slate-700 p-1 rounded-lg transition-colors"
                      >
                        <X size={14} />
                      </button>
                    </div>

                    <div className="bg-white/80 border border-emerald-200/80 rounded-xl p-3 text-xs text-slate-600 leading-relaxed">
                      Are you sure you want to close this ticket? This ticket will be marked as closed and no further replies can be added.
                    </div>

                    <div>
                      <label className="block text-[11px] font-bold text-slate-700 mb-1">
                        Closing note (Optional)
                      </label>
                      <input
                        type="text"
                        placeholder="Add a note before closing..."
                        value={closeNote}
                        onChange={(e) => setCloseNote(e.target.value)}
                        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all shadow-2xs"
                      />
                    </div>

                    <div className="flex items-center justify-end gap-2 pt-1.5 border-t border-emerald-200/60">
                      <button
                        type="button"
                        onClick={() => {
                          setInlineAction("idle");
                          setCloseNote("");
                        }}
                        disabled={loading.action}
                        className="px-3.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-200/60 rounded-xl transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleConfirmClose}
                        disabled={loading.action}
                        className="flex items-center gap-1.5 px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-bold rounded-lg shadow-2xs transition-all"
                      >
                        {loading.action ? <LoaderIcon className="w-3.5 h-3.5" /> : <Check size={13} />}
                        {loading.action ? "Closing..." : "Confirm & Close"}
                      </button>
                    </div>
                  </div>
                ) : (
                  /* 🟢 HR RESOLUTION ACTION FOOTER (Resolved State Default View) */
                  <div className="p-3.5 sm:p-4 border-t border-emerald-200 bg-emerald-50/70 shrink-0 space-y-2.5">
                    <div className="flex items-center justify-between font-bold">
                      <span className="flex items-center gap-1.5 text-xs text-emerald-900">
                        <CheckCircle2 size={15} className="text-emerald-600" /> HR has marked this ticket as resolved
                      </span>
                      <span className="text-[11px] text-emerald-700 font-medium">
                        {selectedTicket.RESOLVED_DATE ? moment(selectedTicket.RESOLVED_DATE).format("DD MMM YYYY, hh:mm A") : ""}
                      </span>
                    </div>

                    {selectedTicket.AUTO_CLOSE_DATE && (
                      <div className="flex items-center gap-1.5 text-[11px] text-amber-900 bg-amber-50/90 px-2.5 py-1.5 rounded-lg border border-amber-200">
                        <Clock size={13} className="text-amber-600 shrink-0" />
                        <span>
                          ⏰ <strong>Auto-Close:</strong> Auto-closing on{" "}
                          <strong>{moment(selectedTicket.AUTO_CLOSE_DATE).format("DD MMM YYYY")}</strong> (2 business days review).
                        </span>
                      </div>
                    )}

                    <div className="pt-0.5 flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setInlineAction("reopen")}
                        className="flex items-center gap-1.5 px-3.5 py-1.5 bg-white hover:bg-amber-50 text-amber-800 font-bold text-xs rounded-lg border border-amber-300 transition-colors shadow-2xs"
                      >
                        <RotateCcw size={13} /> Reopen Ticket
                      </button>
                      <button
                        type="button"
                        onClick={() => setInlineAction("close")}
                        className="flex items-center gap-1.5 px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-lg shadow-2xs transition-colors"
                      >
                        <Check size={13} /> Confirm & Close
                      </button>
                    </div>
                  </div>
                )
              ) : (
                /* Standard Reply Box (For New, In Progress, Waiting for Employee) */
                <div className="p-3.5 sm:p-4 border-t border-slate-100 bg-slate-50/70 shrink-0">
                  <div className="flex flex-col gap-2">
                    <textarea
                      rows={2}
                      placeholder="Type a reply to HR..."
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all resize-none shadow-2xs"
                    />

                    {/* Reply Attachments preview */}
                    {replyAttachments.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {replyAttachments.map((att, idx) => (
                          <div
                            key={idx}
                            className="flex items-center gap-1.5 bg-white border border-slate-200 text-slate-700 px-2.5 py-1 rounded-lg text-xs shadow-2xs"
                          >
                            <FileText size={12} className="text-blue-600" />
                            <span className="max-w-[120px] truncate">{att.name}</span>
                            <button
                              type="button"
                              onClick={() => removeAttachment(idx, "reply")}
                              className="text-slate-400 hover:text-rose-500 ml-0.5"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-0.5">
                      <div>
                        <button
                          type="button"
                          onClick={() => replyFileInputRef.current?.click()}
                          className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-blue-600 transition-colors px-2 py-1.5 rounded-lg hover:bg-slate-200/50"
                        >
                          <Paperclip size={13} /> Attach File
                        </button>
                        <input
                          ref={replyFileInputRef}
                          type="file"
                          multiple
                          className="hidden"
                          onChange={(e) => handleFileUpload(e, "reply")}
                        />
                      </div>

                      <button
                        type="button"
                        onClick={handleSendReply}
                        disabled={loading.reply || (!replyMessage.trim() && replyAttachments.length === 0)}
                        className="flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-bold rounded-xl shadow-xs transition-all"
                      >
                        {loading.reply ? <LoaderIcon className="w-3.5 h-3.5" /> : <Send size={13} />}
                        {loading.reply ? "Sending..." : "Send Reply"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </TailwindModal>
      </div>
    </div>
  );
}